import pick from 'lodash/pick'
import isEqual from 'lodash/isEqual'
import merge from 'lodash/merge'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  FormErrors,
  FormValidatorFn,
  FormValues,
  FormValuesEdited,
  SubmitErrorHandlerFn,
  SubmitHandlerFn,
  UpdateFormOptions,
  UseFormConfig,
  UseFormOptions,
  UseFormReturn,
  ValidateFormFn,
} from './types'
import {
  clearFormState,
  isKeyofTArray,
  retrieveFormState,
  storeFormState,
} from './util'

/**
 * Define form data width validators
 * @param options Form options
 * @param options.values Object defining form data with initial values
 * @param options.validators Object defining validators. For every datum where a validator is not defined, the datum is valid by default.
 * @param options.config Configuration object for useForm
 */
export function useForm<T extends FormValues>(
  options: UseFormOptions<T>
): UseFormReturn<T> {
  /* SETUP */
  const defaultConfig = useMemo<Required<UseFormConfig<{}>>>(
    () => ({
      callbacks: {},
      storage: {
        storeFormState: false,
        storeFormStateName: null,
        storeFormStateKey: null,
        retrieveFormStateOnMount: false,
        retrieveFormStatePrompt:
          'An unsubmitted version of this form was found. Do you wish to restore it?',
        excludeFields: [] as never[],
        sessionStorage: false,
      },
      realtimeValidation: true,
      onlyValidateAfterFirstUpdate: true,
      initAsInvalid: false,
      validateImmediately: false,
      disableValidationInitially: false,
      dontUpdateFormValidForDisabled: false,
      warnOnLocationChange: null,
      onFormChange: () => void 0,
    }),
    []
  )

  const overloadedConfig = useMemo(
    () => merge(defaultConfig, options.config),
    [defaultConfig, options.config]
  )
  const hasCallbacks = Object.keys(overloadedConfig.callbacks).length > 0

  const overloadedOptions = useMemo<Required<UseFormOptions<T>>>(
    () => ({
      validators: {},
      ...options,
      config: overloadedConfig,
    }),
    [options, overloadedConfig]
  )

  /* STATE */
  const firstMount = useRef(true)
  const initialValues = useRef(overloadedOptions.values)

  const initialEdited = Object.keys(overloadedOptions.values).reduce(
    (acc, key) => {
      acc[key as keyof T] = false
      return acc
    },
    {} as FormValuesEdited<T>
  )

  // Keep track of which fields have been edited at least once
  const beenEdited = useRef(initialEdited)

  // What fields have validation disabled?
  const disabledValidation = useRef(
    Object.keys(overloadedOptions.values).reduce((acc, key) => {
      acc[key as keyof T] = !overloadedOptions.config.disableValidationInitially
        ? false
        : overloadedOptions.config.disableValidationInitially === true
        ? true
        : overloadedOptions.config.disableValidationInitially.includes(key)
      return acc
    }, {} as FormValuesEdited<T>)
  )

  const [formValues, setFormValues] = useState<T>(overloadedOptions.values)
  const [formErrors, setFormErrors] = useState<FormErrors<T>>(() =>
    Object.keys(overloadedOptions.values).reduce((acc, key) => {
      acc[key as keyof T] = null
      return acc
    }, {} as FormErrors<T>)
  )
  const [formValid, setFormValid] = useState(!overloadedConfig.initAsInvalid)
  const [formValuesEdited, setFormValuesEdited] = useState<FormValuesEdited<T>>(
    () =>
      Object.keys(overloadedOptions.values).reduce((acc, key) => {
        acc[key as keyof T] = false
        return acc
      }, {} as FormValuesEdited<T>)
  )
  const [formEdited, setFormEdited] = useState(false)

  /**
   * Validate form values
   * @param values Values to validate. If undefined, all current values are used.
   * @param updateFormErrors Whether to update formErrors after validation. Default true
   * @param updateFormValid Whether to update formValid after validation. Default true
   * @param alternativeFormValues Values to use as second argument in validation function. Defaults to current form values
   */
  const validateForm = useCallback<ValidateFormFn<T>>(
    (
      values?: (keyof T)[] | Partial<T>,
      updateFormErrors: boolean = true,
      updateFormValid: boolean = true,
      alternativeFormValues?: Partial<T>
    ) => {
      // Variable initialization
      let internalValues: Partial<T> = {}
      if (typeof values === 'undefined') internalValues = formValues
      else if (isKeyofTArray(values))
        internalValues = pick<Partial<T>>(formValues, values)
      else internalValues = values as Partial<T>

      let internalFormValues = {
        ...formValues,
        ...alternativeFormValues,
      }

      const { validators } = overloadedOptions
      const { current: disabledValidators } = disabledValidation
      const { dontUpdateFormValidForDisabled: skipValidForDisabled } =
        overloadedConfig

      const newErrors: FormErrors<T> = {
        ...formErrors,
      }

      // Iterate over values
      let valid = true
      const valueEntries = Object.entries({
        ...formValues,
        ...internalValues,
      }) as [keyof T, any][]

      for (const [key, value] of valueEntries) {
        const validator = validators[key]

        const disabled = disabledValidators[key]
        const included = typeof internalValues[key] !== 'undefined'

        // For disabled validators, there should always be no error
        if (disabled) newErrors[key] = null

        // If a validator for current key does not exist, error is null and we can skip
        // Or, if we're skipping formValid for disabled validators and this one is disabled, also skip
        if (!validator || (skipValidForDisabled && disabled)) continue

        let currentValid = true

        // If validator is an array of validators, run each function
        if (Array.isArray(validator)) {
          for (const val of validator as FormValidatorFn<typeof value, T>[]) {
            const result = val(value, internalFormValues)
            !disabled && included && (newErrors[key] = result)
            currentValid = currentValid && result === null

            // We can break out of inner loop if we got a string (an error)
            if (result !== null) break
          }
        } else {
          const result = validator(value, internalFormValues)
          !disabled && included && (newErrors[key] = result)
          currentValid = currentValid && result === null
        }

        valid = valid && currentValid
      }

      if (updateFormErrors) setFormErrors(newErrors)
      if (updateFormValid) setFormValid(valid)

      return {
        errors: newErrors,
        valid,
      }
    },
    [formErrors, formValues, overloadedConfig, overloadedOptions]
  )

  const updateFormErrors = useCallback(
    (errors: Partial<FormErrors<T>>, updateValid: boolean = true) => {
      const newErrors = {
        ...formErrors,
        ...errors,
      }
      setFormErrors(() => newErrors)

      if (updateValid) {
        const valid = !Object.values(newErrors).some(v => v !== null)
        if (!valid) setFormValid(valid)
        else validateForm(undefined, false)
      }
    },
    [formErrors, validateForm]
  )

  const updateInitialValues = useCallback(
    (values?: Partial<T>) => {
      const newInitialValues: T = {
        ...formValues,
        ...values,
      }
      initialValues.current = newInitialValues

      // Update beenEdited
      Object.keys(newInitialValues).forEach(
        name => (beenEdited.current[name as keyof T] = false)
      )
      // Update formEdited and formValuesEdited
      setFormEdited(false)
      setFormValuesEdited(beenEdited.current)
    },
    [formValues]
  )

  /**
   * Update form values
   * @param values Partial values object
   * @param runValidation Boolean indicating whether to run validation. Default true.
   */
  const updateForm = useCallback(
    (values: Partial<T>, options: UpdateFormOptions<T> = {}) => {
      options = {
        runCallbacks: true,
        ...options,
      }

      const updatedValues = {
        ...formValues,
        ...values,
      }

      // Update beenEdited
      Object.keys(values).forEach(
        name => (beenEdited.current[name as keyof T] = true)
      )

      // Check edited values
      const editedValues = Object.entries(updatedValues).reduce(
        (acc, [name, value]) => {
          const initialValue = initialValues.current[name]
          acc[name as keyof T] = !isEqual(value, initialValue)
          return acc
        },
        {} as Partial<FormValuesEdited<T>>
      )

      setFormValuesEdited(editedValues as FormValuesEdited<T>)
      setFormEdited(Object.values(editedValues).some(v => v))

      setFormValues(updatedValues)
      overloadedConfig.onFormChange(updatedValues)

      // Value callbacks
      if (options.runCallbacks && hasCallbacks)
        for (const [key, value] of Object.entries(values))
          if (typeof value !== 'undefined')
            overloadedConfig.callbacks[key]?.(value, updatedValues)

      // Storing form state
      if (
        options.storeFormState === true ||
        (overloadedConfig.storage.storeFormState &&
          options.storeFormState !== false)
      )
        storeFormState(updatedValues, overloadedConfig.storage)

      // Prepare validation: add values from options.includeValidation
      let updatedValuesForValidation: Partial<T> = updatedValues
      if (typeof options.includeValidation !== 'undefined') {
        for (const key in options.includeValidation) {
          const value =
            updatedValuesForValidation[key as keyof T] ??
            formValues[key as keyof T]

          updatedValuesForValidation = {
            ...updatedValuesForValidation,
            [key]: value,
          }
        }
      }

      // Validation
      if (
        options.runValidation === true ||
        (overloadedConfig.realtimeValidation && options.runValidation !== false)
      )
        if (overloadedConfig.onlyValidateAfterFirstUpdate) {
          // Extract only values which have been edited at least once
          const valuesToValidate = Object.entries(
            updatedValuesForValidation
          ).reduce((acc, [name, value]) => {
            const isEdited = beenEdited.current[name]
            if (isEdited) acc[name as keyof T] = value
            return acc
          }, {} as Partial<T>)

          validateForm(valuesToValidate, true, true, updatedValues)
        } else
          validateForm(updatedValuesForValidation, true, true, updatedValues)

      // Update initial values
      if (options.updateInitialValues === true)
        updateInitialValues(updatedValues)
    },
    [
      formValues,
      hasCallbacks,
      overloadedConfig,
      updateInitialValues,
      validateForm,
    ]
  )

  /**
   * Create a change handler for a form value
   * @param key Name of the form value for which to create a change handler
   * @param runValidation Boolean indicating whether to run validation. Default true.
   */
  const formChangeHandler = useCallback(
    <K extends keyof T>(key: K, options?: UpdateFormOptions<T>) => {
      return (value: T[K]) =>
        updateForm({ [key]: value } as unknown as Partial<T>, options)
    },
    [updateForm]
  )

  /**
   * Reset entire form to initial state
   */
  const resetForm = useCallback(() => {
    const values = initialValues.current

    setFormValues(values)

    setFormErrors(() =>
      Object.keys(values).reduce((acc, key) => {
        acc[key as keyof T] = null
        return acc
      }, {} as FormErrors<T>)
    )
    setFormValid(!overloadedConfig.initAsInvalid)

    const valuesEdited = Object.keys(values).reduce((acc, key) => {
      acc[key as keyof T] = false
      return acc
    }, {} as FormValuesEdited<T>)
    beenEdited.current = valuesEdited

    setFormValuesEdited(valuesEdited)
    setFormEdited(false)
  }, [overloadedConfig.initAsInvalid])

  /** Load form data stored in localStorage */
  const loadStoredFormState = useCallback(
    (insert: boolean = true) => {
      const retrieved = retrieveFormState(
        overloadedOptions.values,
        overloadedConfig.storage
      )
      if (retrieved === null) return null

      if (insert)
        updateForm(retrieved, {
          runCallbacks: false,
        })

      return retrieved
    },
    [overloadedOptions.values, overloadedConfig.storage, updateForm]
  )

  /** Clear form data stored in localStorage */
  const clearStoredFormState = useCallback(() => {
    clearFormState(overloadedConfig.storage)
  }, [overloadedConfig.storage])

  const enableValidation = useCallback(
    (key: keyof T, run: boolean = true, enable: boolean = true) => {
      if (!enable === disabledValidation.current[key]) return

      disabledValidation.current = {
        ...disabledValidation.current,
        [key]: !enable,
      }

      if (run) validateForm([key])
    },
    [validateForm]
  )

  const submitHandler = useCallback(
    (
      handler: SubmitHandlerFn<T>,
      errorHandler: SubmitErrorHandlerFn<T> = () => void 0
    ) => {
      return () => {
        const { valid, errors } = validateForm()
        if (valid) {
          handler(formValues)
          clearStoredFormState()
        } else errorHandler(errors)
      }
    },
    [formValues, clearStoredFormState, validateForm]
  )

  /* Retrieve form state from localStorage on first mount and validate immediately */
  useEffect(() => {
    if (!firstMount.current) return
    firstMount.current = false

    if (overloadedConfig.validateImmediately) validateForm()

    if (
      !overloadedConfig.storage.storeFormState ||
      !overloadedConfig.storage.retrieveFormStateOnMount
    )
      return

    const retrieved = loadStoredFormState(false)
    if (retrieved === null) return

    if (overloadedConfig.storage.retrieveFormStateOnMount === 'ask')
      if (!window.confirm(overloadedConfig.storage.retrieveFormStatePrompt))
        return

    updateForm(retrieved, {
      runCallbacks: false,
    })
  }, [
    overloadedConfig.storage.storeFormState,
    overloadedConfig.storage.storeFormStateName,
    overloadedConfig.storage.storeFormStateKey,
    overloadedConfig.storage.retrieveFormStateOnMount,
    overloadedConfig.storage.retrieveFormStatePrompt,
    overloadedConfig.validateImmediately,
    overloadedOptions.values,
    updateForm,
    loadStoredFormState,
    validateForm,
    formValues,
  ])

  return {
    formValues,
    formErrors,
    formValid,
    formEdited,
    formValuesEdited,
    updateForm,
    validateForm,
    resetForm,
    updateFormErrors,
    updateInitialValues,
    formChangeHandler,
    loadStoredFormState,
    clearStoredFormState,
    enableValidation,
    submitHandler,
  }
}
